// src/pages/Archive/ArchiveDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Assignment,
  Build,
  Speed,
  FolderOpen,
  Analytics,
  Timeline
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';

const ArchiveCard = ({ title, description, icon: Icon, color = "primary", onClick, count = 0 }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
        border: `2px solid ${alpha(theme.palette[color].main, 0.1)}`,
        borderRadius: 3
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar sx={{ 
            bgcolor: alpha(theme.palette[color].main, 0.1), 
            width: 60, 
            height: 60,
            mr: 2,
            border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`
          }}>
            <Icon sx={{ color: theme.palette[color].main, fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
            {count > 0 && (
              <Chip 
                label={`${count} registros`}
                color={color}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          color={color}
          fullWidth
          onClick={onClick}
          sx={{ 
            height: 44,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Ver archivo
        </Button>
      </CardContent>
    </Card>
  );
};

export default function ArchiveDashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const theme = useTheme();

  // Додаємо функцію для fallback перекладів
  const tt = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const [stats, setStats] = useState({
    operatorReports: 0,
    technicianReports: 0,
    meterRecords: 0,
    totalRecords: 0
  });

  const loadStats = async () => {
    try {
      const allRecords = await getData('lainsa_records') || [];
      
      const operatorReports = allRecords.filter(record => 
        record.recordType === 'shift_report' || 
        (record.tunel && record.rech_tunel)
      ).length;

      const technicianReports = allRecords.filter(record => 
        record.recordType === 'technician_report' || 
        record.recordType === 'maintenance' ||
        (record.technician && !record.tunel)
      ).length;

      const meterRecords = allRecords.filter(record => 
        (record.cont && Object.keys(record.cont).length > 0) ||
        record.recordType === 'meter_reading'
      ).length;

      setStats({
        operatorReports,
        technicianReports,
        meterRecords,
        totalRecords: allRecords.length
      });
    } catch (error) {
      console.error('Error loading archive stats:', error);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const archiveSections = [
    {
      title: tt('archive.tabs.operator', 'Reportes Operador'),
      description: tt('archive.operator_reports_desc', 'Reportes de producción y turnos completados'),
      icon: Assignment,
      color: 'primary',
      path: '/archive/operator',
      count: stats.operatorReports
    },
    {
      title: tt('archive.tabs.technician', 'Reportes Técnico'),
      description: tt('archive.technician_reports', 'Reportes técnicos y mantenimiento'),
      icon: Build,
      color: 'secondary',
      path: '/archive/technician',
      count: stats.technicianReports
    },
    {
      title: tt('archive.tabs.meters', 'Contadores'),
      description: 'Historial de consumo de agua y propano',
      icon: Speed,
      color: 'info',
      path: '/archive/meters',
      count: stats.meterRecords
    },
    {
      title: tt('archive.tabs.misc', 'Otros Materiales'),
      description: 'Documentación y materiales adicionales',
      icon: FolderOpen,
      color: 'warning',
      path: '/archive/misc',
      count: 0
    },
    {
      title: 'Análisis Completo',
      description: 'Vista unificada de todos los registros',
      icon: Analytics,
      color: 'success',
      path: '/archive-old',
      count: stats.totalRecords
    },
    {
      title: 'Tendencias',
      description: 'Análisis histórico y comparativas',
      icon: Timeline,
      color: 'error',
      path: '/analytics',
      count: 0
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          {tt('archive.title', 'Archivo Central')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {tt('archive.subtitle', 'Acceso centralizado a todos los tipos de informes y datos históricos')}
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {stats.operatorReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reportes Operador
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            borderRadius: 3
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: 'secondary.main' }}>
                {stats.technicianReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reportes Técnico
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 3
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: 'info.main' }}>
                {stats.meterRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registros Contadores
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 3
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: 'success.main' }}>
                {stats.totalRecords}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Registros
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Archive Sections */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Secciones del Archivo
      </Typography>
      
      <Grid container spacing={3}>
        {archiveSections.map((section, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <ArchiveCard
              title={section.title}
              description={section.description}
              icon={section.icon}
              color={section.color}
              count={section.count}
              onClick={() => navigate(section.path)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.default', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Acciones Rápidas
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/archive/operator')}
          >
            Ver Reportes Operador
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={() => navigate('/analytics')}
          >
            Análisis Avanzado
          </Button>
          <Button 
            variant="outlined" 
            color="info"
            onClick={() => navigate('/reports')}
          >
            Generar Reportes
          </Button>
          <Button 
            variant="outlined" 
            color="warning"
            onClick={loadStats}
          >
            Actualizar Estadísticas
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}


